import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { DT } from '../../theme/designTokens';
import { GameplayBackdrop } from '../ui/GameplayBackdrop';
import { PlayerAvatar } from '../ui/PlayerAvatar';
import { CountryFlag } from '../ui/CountryFlag';
import { getViewerHandAssetPath } from '../../config/viewerTileSkinAssets';
import { getDominoBackAssetPath } from '../../config/dominoAssets';
import { getSkinBackAssetPath } from '../../config/viewerTileSkinAssets';
import type { ViewerTileSkin } from '../../config/viewerCosmetics';
import type { RoundEndPayload, OrdinaryReadyStatus } from '../../types/roundEnd';
import type { RevealedHandTile, Player, RevealedPlayer } from '../../types/game';
import { useTranslation } from '../../i18n/react';
import { t as translate } from '../../i18n';
import { getPlayerName } from '../../utils/playerName';

/**
 * OrdinaryRoundEndOverlay — the redesigned ordinary (session-continues)
 * Round End experience. Renders inside the canonical GameplayBackdrop
 * (Phase 2), which keeps the finished Phaser board mounted and blurred
 * behind this panel for the whole visible lifetime, including the exit.
 *
 * Final Match End is a SEPARATE, byte-identical component (RoundEndOverlay)
 * — this file never renders for a sessionOver payload and shares none of its
 * presentation code, by design (isolation boundary from the approved plan).
 */

const COUNTDOWN_SECONDS = 15;
const MAX_VISUAL_BACKS   = 7; // starting hand size — the true cap on any hand's tile count
/** Localized team labels, resolved at call time (render re-runs on switch). */
const teamLabel = (team: 0 | 1): string =>
  String(translate(team === 0 ? 'gameplay:team.a' : 'gameplay:team.b'));
const TEAM_COLOR = [DT.color.teamRed, DT.color.teamBlue] as const;
const PANEL_SURFACE = '#181410'; // warm near-black, reference-inspired — presentation-local, not a shared token

interface Props {
  payload:          RoundEndPayload;
  /** Mark THIS viewer ready for the next round (= useRoundEndOverlay.markReady). */
  onReady:          (sessionId: number) => Promise<void>;
  readyStatus:      OrdinaryReadyStatus;
  /** True once the server has announced the next round — GameplayBackdrop
   *  begins its canonical exit while this panel's content stays mounted. */
  exiting:          boolean;
  /** Wired to GameplayBackdrop.onExited — completes the Phaser/React handshake. */
  onOrdinaryExited: () => void;
  viewerTileSkin?:  ViewerTileSkin;
}

function backAssetPath(skin: ViewerTileSkin = 'classic'): string {
  return skin === 'classic' ? getDominoBackAssetPath() : getSkinBackAssetPath(skin);
}

// ── Tile art — vertical orientation with a correctly-sized layout box ───────
//
// PROVEN DISTORTION CAUSE (verified against the actual Tailwind v4 preflight
// shipped in node_modules/tailwindcss/preflight.css):
//   img, video { max-width: 100%; height: auto; }
// This is a plain type-selector rule (specificity 0,0,0,1) with no
// `!important`. `max-width` structurally CAPS the resolved width regardless
// of cascade origin — an inline `style={{ width: 56 }}` on the <img> does NOT
// defeat it, because max-width isn't a normal "who wins the cascade" fight,
// it constrains whatever width would otherwise be used. With the tile's
// absolutely-positioned wrapper only 28px wide, the previous inline
// `width: 56` was capped down to 28, while the inline `height: 28` (which
// *does* out-specificity the non-!important `height: auto`) stayed at 28 —
// producing a squashed ~1:1 box instead of the intended 2:1 pre-rotation box,
// visibly distorting every pip and the center divider.
//
// Fix: size the wrapper AND image entirely through this component's own
// injected stylesheet (ORDINARY_STYLES, below) using a class selector
// (`.ore-tile img`, specificity 0,0,1,1) — genuinely MORE specific than
// Tailwind's bare `img` selector (0,0,0,1), so it wins on specificity alone,
// not just style order. `max-width: none` / `max-height: none` additionally
// remove Tailwind's cap outright as defense in depth. The wrapper uses flex
// centering (not absolute + translate) so the pre-rotation image box — set to
// the EXACT 2:1 ratio of the source asset, uniformly scaled, never
// independently stretched — rotates around its own center with no extra
// scale transform, landing exactly inside the wrapper's reserved box.
function RevealedTile({ tile, delay, viewerTileSkin }: { tile: RevealedHandTile; delay: number; viewerTileSkin?: ViewerTileSkin }) {
  const [failed, setFailed] = useState(false);
  const useClassic = failed || viewerTileSkin === 'classic' || !viewerTileSkin;
  const skin = useClassic ? 'classic' : viewerTileSkin;
  return (
    <span className="ore-tile" style={{ animationDelay: `${delay}ms` }}>
      <img
        src={getViewerHandAssetPath(skin, tile.value_a, tile.value_b)}
        alt={`Domino ${tile.value_a}-${tile.value_b}`}
        onError={!useClassic ? () => setFailed(true) : undefined}
      />
    </span>
  );
}

function BackTile() {
  return (
    <span className="ore-tile" aria-hidden="true">
      <img src={backAssetPath('classic')} alt="" />
    </span>
  );
}

function SeatAvatar({ name, team, avatarKey }: { name: string; team: number | null; avatarKey?: string | null }) {
  const bg = team !== null ? TEAM_COLOR[team as 0 | 1] : DT.color.textMuted;
  return (
    <PlayerAvatar
      avatarKey={avatarKey}
      name={name}
      size={30}
      loading="eager"
      initialStyle={{
        background: bg, border: 'none',
        fontSize: 12, fontWeight: 700, fontFamily: DT.font.sans,
        color: '#fff', opacity: 0.92,
      }}
    />
  );
}

function DominoedBadge() {
  return (
    <span style={{
      display:       'inline-flex',
      alignItems:    'center',
      padding:       '2px 8px',
      borderRadius:  DT.radius.pill,
      background:    DT.color.successBg,
      border:        `1px solid ${DT.color.success}`,
      fontSize:      9,
      fontWeight:    700,
      fontFamily:    DT.font.sans,
      color:         DT.color.success,
      letterSpacing: '0.06em',
      textTransform: 'uppercase' as never,
      whiteSpace:    'nowrap' as never,
    }}>
      {translate('gameplay:roundEnd.dominoedBadge')}
    </span>
  );
}

/** One stable player row — real revealed tiles when available, face-down
 *  count-only backs otherwise. Never reorders; the dominoed/winning row is
 *  highlighted IN PLACE via a success-tinted background/border. */
function PlayerRow({
  name, team, tiles, count, isDomino, isWinner, rowDelay, viewerTileSkin, avatarKey, countryCode, isBot, tutorialTarget,
}: {
  /** Tutorial spotlight registration (inert data attribute; see
   *  tutorial/spotlight/domTargets.ts). */
  tutorialTarget?: string;
  name: string; team: number | null;
  tiles: RevealedHandTile[] | null; count: number | null;
  isDomino: boolean; isWinner: boolean; rowDelay: number; viewerTileSkin?: ViewerTileSkin;
  avatarKey?: string | null; countryCode?: string | null; isBot?: boolean;
}) {
  const borderColor = isDomino ? DT.color.success : (isWinner ? DT.color.borderGoldStrong : DT.color.border);
  const nameColor   = isWinner ? DT.color.gold : DT.color.textPrimary;
  const revealed    = tiles !== null;
  const visibleBackCount = count !== null ? Math.min(count, MAX_VISUAL_BACKS) : 0;

  return (
    <div style={{
      background:   isDomino ? DT.color.successBg : DT.color.cardBgElevated,
      border:       `1px solid ${borderColor}`,
      borderLeft:   team !== null ? `3px solid ${TEAM_COLOR[team as 0 | 1]}` : `1px solid ${borderColor}`,
      borderRadius: DT.radius.md,
      // ONE organized row: identity left, vertical tile hand in the middle,
      // status/badge right — not a two-line stack. Height is content-driven
      // (no forced minHeight): ~62-66px when a full tile column is present
      // (52px tile + 2×6px padding), naturally shorter for the dominoed/
      // no-tiles row.
      padding:      '6px 12px',
      display:      'flex',
      flexDirection:'row',
      alignItems:   'center',
      gap:          10,
      animation:    `ore-row-in 280ms ${DT.motion.easing} both`,
      animationDelay: `${rowDelay}ms`,
    }} className="ore-row" data-tutorial-target={tutorialTarget}>

      {/* LEFT: identity — flag, avatar, name. Bots reserve the flag slot's
          width (empty) so every row's tile column starts at the same X. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {isBot
          ? <span aria-hidden="true" style={{ width: 16, flexShrink: 0 }} />
          : <CountryFlag countryCode={countryCode} width={16} />}
        <SeatAvatar name={name} team={team} avatarKey={avatarKey} />
        <div style={{
          width: 80, flexShrink: 0, fontSize: 12, fontWeight: 600, fontFamily: DT.font.sans,
          color: nameColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {name}
        </div>
      </div>

      {/* MIDDLE: the vertical tile hand — real revealed tiles, or face-down
          backs, or nothing (dominoed row). Never flex-shrinks per tile;
          wraps only when the row genuinely lacks width for all of them. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, flex: '1 1 auto' }}>
        {revealed && tiles!.map((t, ti) => (
          <RevealedTile key={t.id} tile={t} delay={rowDelay + 60 + ti * 35} viewerTileSkin={viewerTileSkin} />
        ))}
        {!revealed && !isDomino && Array.from({ length: visibleBackCount }, (_, i) => <BackTile key={i} />)}
      </div>

      {/* RIGHT: dominoed badge and/or pip-or-count status. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {isDomino && <DominoedBadge />}
        {revealed ? (
          // Live data: the REAL pip total (value_a + value_b per tile) —
          // never tile count. Correctly reads "0 pips" for the dominoed
          // player's empty hand, shown alongside the badge above.
          <span style={{ fontSize: 10, fontFamily: DT.font.sans, color: DT.color.textMuted }}>
            {pipLabel(tiles!)}
          </span>
        ) : count !== null ? (
          // Reconnect count-only: tile COUNT, never a fabricated pip value.
          <span style={{ fontSize: 10, fontFamily: DT.font.sans, color: DT.color.textMuted }}>
            {translate('gameplay:roundEnd.tiles', { count })}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Pip total for a revealed hand (sum of value_a + value_b per tile) — 0 for
 *  the dominoed player's empty hand. */
function pipTotal(tiles: RevealedHandTile[]): number {
  return tiles.reduce((s, t) => s + t.value_a + t.value_b, 0);
}

function pipLabel(tiles: RevealedHandTile[]): string {
  return String(translate('gameplay:roundEnd.pips', { count: pipTotal(tiles) }));
}

function HandSection({
  players, revealedPlayers, handCounts, winnerTeam, winType, viewerTileSkin,
}: Pick<RoundEndPayload, 'players' | 'revealedPlayers' | 'handCounts' | 'winnerTeam' | 'winType'> & { viewerTileSkin?: ViewerTileSkin }) {
  const hasRevealed = revealedPlayers.length > 0;
  const hasPlayers  = players.length > 0;
  const hasCounts   = Object.keys(handCounts).length > 0;

  // Incomplete-data fallback: no fabricated rows — omit the section cleanly.
  if (!hasPlayers && !hasCounts) return null;

  const rowsFromPlayers = hasPlayers ? [...players].sort((a, b) => a.position - b.position) : [];
  const countOnlyPositions = !hasPlayers
    ? Object.keys(handCounts).sort((a, b) => Number(a) - Number(b))
    : [];

  const sectionLabel = translate(hasRevealed ? 'gameplay:roundEnd.handsRevealed' : 'gameplay:roundEnd.remainingTiles');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          fontSize: 10, fontWeight: 700, fontFamily: DT.font.sans,
          letterSpacing: '0.3em', textTransform: 'uppercase', color: DT.color.textMuted,
        }}>
          {sectionLabel}
        </div>
        <div aria-hidden="true" style={{ flex: 1, height: 1, background: DT.color.border }} />
      </div>

      {/* Count-only path: no players list at all (severely incomplete data),
          synthesize seat rows from handCounts positions only. */}
      {countOnlyPositions.map((posStr, i) => {
        const pos      = Number(posStr);
        const count    = handCounts[posStr];
        const rp       = revealedPlayers.find(r => r.position === pos);
        const isDomino = winType === 'win' && count === 0;
        const name     = rp ? getPlayerName(rp, `Seat ${pos + 1}`) : `Seat ${pos + 1}`;
        return (
          <PlayerRow
            key={posStr}
            name={name}
            team={null}
            tiles={null}
            count={count}
            isDomino={isDomino}
            isWinner={false}
            rowDelay={i * 70}
            avatarKey={rp?.avatar_key ?? null}
            countryCode={rp?.country_code ?? null}
            isBot={rp?.is_bot ?? false}
          />
        );
      })}

      {/* Stable canonical seat order — the winning/dominoed player is
          highlighted IN PLACE, never reordered to the top. */}
      {rowsFromPlayers.map((player: Player, i: number) => {
        const rp       = revealedPlayers.find((r: RevealedPlayer) => r.position === player.position);
        const count    = handCounts[String(player.position)] ?? null;
        const isWinner = winnerTeam !== null && player.team === winnerTeam;
        const isDomino = winType === 'win'
          && player.team === winnerTeam
          && (rp !== undefined ? rp.tiles.length === 0 : count === 0);
        const name        = getPlayerName(rp ?? player, `P${player.position}`);
        const avatarKey    = rp?.avatar_key ?? player.avatar_key ?? null;
        const countryCode  = rp?.country_code ?? player.country_code ?? null;
        const isBot        = rp?.is_bot ?? player.is_bot ?? false;

        return (
          <PlayerRow
            key={player.position}
            tutorialTarget={`roundend-row-team-${player.team}`}
            name={name}
            team={player.team}
            tiles={rp !== undefined ? rp.tiles : null}
            count={isDomino ? 0 : count}
            isDomino={isDomino}
            isWinner={isWinner}
            rowDelay={i * 70}
            viewerTileSkin={viewerTileSkin}
            avatarKey={avatarKey}
            countryCode={countryCode}
            isBot={isBot}
          />
        );
      })}
    </div>
  );
}

const ORDINARY_STYLES = `
.ore-center {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
}
.ore-panel {
  /* Reference-proportional width (the 2560×1292 reference's panel occupies
     ~25-30% of viewport width): scales with the viewport instead of pinning
     a fixed pixel value, clamped to a sane desktop range. NOT reused as a
     fixed dimension — genuinely responsive. */
  width: clamp(480px, 28vw, 660px);
  /* height is intentionally UNSET (natural/auto) — the panel is exactly as
     tall as its COMPACT content requires. On a normal desktop viewport
     (~800-950px content height) the compact metrics below make the content
     fit before ever reaching max-height — overflow-y:auto is a FALLBACK
     for genuinely short/mobile viewports, not the normal desktop solution.
     GameplayBackdrop's own outer scroll layer provides a second, viewport-
     level fallback on top of this (harmless to nest). */
  max-height: calc(100dvh - 24px);
  overflow-y: auto;
  box-sizing: border-box;
  padding: 22px;
  border-radius: ${DT.radius.lg}px;
  background: ${PANEL_SURFACE};
  border: 1px solid ${DT.color.borderGold};
  box-shadow: ${DT.shadow.gold};
  display: flex;
  flex-direction: column;
  gap: 12px;
}
/* Compact tier: narrow (mobile/tablet) OR short (landscape/small-height
   desktop) viewports both get the same tighter density — reusing one rule
   set rather than a separate component tree or duplicated breakpoints. */
@media (max-width: 640px), (max-height: 900px) {
  .ore-center { padding: 16px; }
  .ore-panel  { padding: 18px; gap: 10px; }
}

/* ── Tile art sizing — see the RevealedTile/BackTile comment for the proven
   distortion cause. This class selector (specificity 0,0,1,1) genuinely
   outranks Tailwind preflight's bare img selector (0,0,0,1);
   max-width/max-height: none additionally remove that cap outright. Flex
   centering (not absolute positioning) lets the pre-rotation image box —
   sized to the source asset's exact 2:1 ratio, uniformly, never stretched —
   rotate around its own center and land exactly inside the wrapper. */
.ore-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 52px;
  flex: 0 0 auto;
}
.ore-tile img {
  display: block;
  width: 52px;
  height: 26px;
  max-width: none;
  max-height: none;
  object-fit: contain;
  transform: rotate(90deg);
  transform-origin: center;
  border-radius: 3px;
  image-rendering: crisp-edges;
}
@media (max-width: 640px), (max-height: 900px) {
  .ore-tile     { width: 24px; height: 48px; }
  .ore-tile img { width: 48px; height: 24px; }
}

/* Ready button: structural/responsive sizing only. Dynamic (state-dependent)
   styling — background/color/shadow/cursor/opacity — stays inline, driven
   by readyStatus. Centered, width-constrained on DESKTOP WIDTH ALONE —
   never conditioned on viewport height, so a short desktop/laptop window
   still gets the compact centered button, not the mobile full-width one.
   Full-width only below the 640px WIDTH breakpoint (mobile/narrow), which
   matches .ore-center/.ore-panel's own width breakpoint. This rule is
   deliberately NOT part of the shared (max-width:640px), (max-height:900px)
   compact-density query above — that query may still shrink OTHER metrics
   (tiles, padding) on a short-but-wide desktop, but it must never touch
   .ore-ready-btn, so a short desktop can never fall back to width:100%. */
.ore-ready-btn {
  width: 100%;
  align-self: stretch;
}
@media (min-width: 641px) {
  .ore-ready-btn {
    width: 300px;
    align-self: center;
  }
}

@keyframes ore-row-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes ore-tile-in {
  from { opacity: 0; transform: scale(0.85); }
  to   { opacity: 1; transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .ore-row { animation: none !important; }
}
`;

export function OrdinaryRoundEndOverlay({ payload, onReady, readyStatus, exiting, onOrdinaryExited, viewerTileSkin }: Props) {
  const { t } = useTranslation('gameplay');
  const {
    roundNumber, winnerTeam, winType, sessionId,
    teamScores, previousTeamScores,
    players, revealedPlayers, handCounts,
    targetScore, nextRoundDeadlineAt,
  } = payload;

  const resultTitleId = useId();
  const resultDescId  = useId();
  const readyButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Initial focus target: the TOP of the panel (the result heading), not the
  // Ready button. Focusing an off-screen-below element (the button, at the
  // bottom of a tall panel) makes the browser scroll it into view on open,
  // hiding the round label/result/score card above it. tabIndex={-1} makes
  // this non-interactive heading programmatically focusable without adding
  // it to the normal Tab order (GameplayBackdrop's own trap already excludes
  // [tabindex="-1"] elements from FOCUSABLE) — Tab still reaches the Ready
  // button first, exactly as before.
  const topFocusRef = useRef<HTMLDivElement>(null);

  // Countdown — DISPLAY-ONLY, derived strictly from the server deadline. No
  // client-authoritative timer is ever created; absence hides the numeric line.
  const deadlineMs = useMemo(
    () => (nextRoundDeadlineAt ? Date.parse(nextRoundDeadlineAt) : null),
    [nextRoundDeadlineAt],
  );
  const remaining = useCallback(
    () => (deadlineMs == null ? null : Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000))),
    [deadlineMs],
  );
  const [countdown, setCountdown] = useState<number | null>(remaining);
  useEffect(() => {
    if (deadlineMs == null) { setCountdown(null); return; }
    setCountdown(remaining());
    const t = setInterval(() => setCountdown(remaining()), 250);
    return () => clearInterval(t);
  }, [deadlineMs, remaining]);

  const handleReady = useCallback(() => { void onReady(sessionId); }, [onReady, sessionId]);

  const dominoPlayer = (() => {
    if (winType !== 'win') return null;
    const rp = revealedPlayers.find(r => r.tiles.length === 0);
    if (rp) return getPlayerName(rp);
    const zeroPos = Object.entries(handCounts).find(([, v]) => v === 0)?.[0];
    if (zeroPos == null) return null;
    const p = players.find(pl => String(pl.position) === zeroPos);
    return p ? getPlayerName(p, String(translate('gameplay:fallback.player'))) : null;
  })();

  const isTie = winType === 'blocked' && winnerTeam === null;
  const winnerColor = winnerTeam !== null ? DT.color.gold : DT.color.textSecondary;
  const delta = (team: number) => (teamScores[String(team)] ?? 0) - (previousTeamScores[String(team)] ?? 0);

  const resultTitle = winnerTeam !== null
    ? t('roundEnd.teamWins', { team: teamLabel(winnerTeam as 0 | 1) })
    : (isTie ? t('roundEnd.tied') : t('roundEnd.blocked'));
  const resultSubtitle =
    winType === 'win' && dominoPlayer     ? t('roundEnd.dominoedBy', { name: dominoPlayer })
    : winType === 'blocked' && !isTie     ? t('roundEnd.blockedCounted')
    : winType === 'blocked' && isTie      ? t('roundEnd.blockedTied')
    : '';

  const readyLabel =
    readyStatus === 'confirmed' ? t('roundEnd.readyDone')
    : readyStatus === 'pending' ? '' // spinner only — accessible name via aria-label below
    : t('roundEnd.imReady');
  const readyDisabled = readyStatus === 'pending' || readyStatus === 'confirmed';

  return (
    <GameplayBackdrop
      active={!exiting}
      onExited={onOrdinaryExited}
      initialFocusRef={topFocusRef}
      panelRef={panelRef}
      ariaLabelledBy={resultTitleId}
      ariaDescribedBy={resultDescId}
    >
      <style>{ORDINARY_STYLES}</style>
      <div className="ore-center">
        <div className="ore-panel" data-tutorial-target="roundend-panel">

          {/* Header — never a fixed total-round count. */}
          <div style={{
            textAlign: 'center', fontSize: 10, fontWeight: 700, fontFamily: DT.font.sans,
            letterSpacing: '0.25em', textTransform: 'uppercase', color: DT.color.textMuted,
          }}>
            {t('roundEnd.roundHeader', { n: roundNumber, target: targetScore })}
          </div>

          {/* Result headline + subtitle. The heading also receives initial
              focus (see topFocusRef above) so the overlay opens scrolled to
              the TOP — showing round label/result/score card — instead of
              jumping to the Ready button below. */}
          <div style={{ textAlign: 'center' }}>
            <div id={resultTitleId} ref={topFocusRef} tabIndex={-1} style={{
              fontSize: 28, fontWeight: 400, fontFamily: DT.font.heading, lineHeight: 1.1, color: winnerColor,
              outline: 'none',
            }}>
              {resultTitle}
            </div>
            <div id={resultDescId} style={{
              fontSize: 12, fontFamily: DT.font.sans, color: DT.color.textMuted, marginTop: 4, minHeight: 14,
            }}>
              {resultSubtitle}
            </div>
          </div>

          {/* Score card — target ~70-90px total height: two compact team
              rows (label+score+delta on one line, a thin progress bar below)
              plus the caption, tight padding, no excess vertical space. */}
          <div data-tutorial-target="roundend-scores" style={{
            background: DT.color.cardBg, border: `1px solid ${DT.color.border}`,
            borderRadius: DT.radius.lg, padding: '10px 12px',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            {([0, 1] as const).map(team => {
              const after    = teamScores[String(team)] ?? 0;
              const d        = delta(team);
              const isWinner = winnerTeam === team;
              const numColor = isWinner ? DT.color.gold : DT.color.textPrimary;
              const pct      = targetScore > 0 ? Math.max(0, Math.min(100, (after / targetScore) * 100)) : 0;
              return (
                <div key={team} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div aria-hidden="true" style={{
                      width: 6, height: 6, borderRadius: '50%', background: TEAM_COLOR[team], flexShrink: 0,
                    }} />
                    <div style={{
                      width: 60, flexShrink: 0, fontSize: 11, fontWeight: 700, fontFamily: DT.font.sans,
                      color: TEAM_COLOR[team], letterSpacing: '0.08em', textTransform: 'uppercase',
                    }}>
                      {teamLabel(team as 0 | 1)}
                    </div>
                    <div style={{
                      flex: 1, display: 'flex', alignItems: 'baseline', gap: 5,
                      fontFamily: DT.font.heading, fontSize: 17,
                    }}>
                      <span style={{ color: numColor }}>{after}</span>
                      {d > 0 && (
                        <span style={{ fontSize: 11, fontFamily: DT.font.sans, fontWeight: 700, color: DT.color.success }}>
                          +{d}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Decorative progress bar — the score number above is the
                      accessible equivalent; this is hidden from AT. */}
                  <div aria-hidden="true" style={{
                    height: 3, borderRadius: 2, background: DT.color.border, overflow: 'hidden',
                  }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: TEAM_COLOR[team], borderRadius: 2 }} />
                  </div>
                </div>
              );
            })}
            {targetScore > 0 && (
              <div style={{ fontSize: 10, fontFamily: DT.font.sans, color: DT.color.textFaint, textAlign: 'right' }}>
                {t('roundEnd.raceTo', { target: targetScore })}
              </div>
            )}
          </div>

          {/* Revealed hands / remaining tiles */}
          <HandSection
            players={players}
            revealedPlayers={revealedPlayers}
            handCounts={handCounts}
            winnerTeam={winnerTeam}
            winType={winType}
            viewerTileSkin={viewerTileSkin}
          />

          {/* Ready action + status. The button's structural sizing (width,
              centering) is in the .ore-ready-btn class — compact/centered
              on desktop, full-width on mobile/short viewports (see
              ORDINARY_STYLES) — while its STATE-dependent styling
              (background/color/shadow/cursor/opacity, driven by
              readyStatus) stays inline, unchanged in behavior. */}
          {/* Note: intentionally NOT alignItems:'center' on this column — the
              text lines below must keep stretching full-width so they wrap
              normally; only the button opts out via its own .ore-ready-btn
              align-self (desktop: center + fixed width; mobile: stretch). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <button
              ref={readyButtonRef}
              type="button"
              onClick={handleReady}
              disabled={readyDisabled}
              aria-label={readyStatus === 'pending' ? t('roundEnd.confirmingAria') : undefined}
              className="ore-ready-btn"
              style={{
                minHeight: 42, padding: '8px 20px', borderRadius: 12, border: 'none',
                background: readyDisabled
                  ? (readyStatus === 'confirmed' ? DT.color.successBg : 'rgba(201,168,76,0.25)')
                  : DT.gradient.gold,
                boxShadow:  readyDisabled ? 'none' : DT.shadow.gold,
                color:      readyStatus === 'confirmed' ? DT.color.success : DT.color.goldOnGold,
                fontSize: 14, fontWeight: 600, fontFamily: DT.font.sans,
                letterSpacing: '0.16em', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                cursor: readyDisabled ? 'default' : 'pointer',
                opacity: readyDisabled && readyStatus !== 'confirmed' ? 0.55 : 1,
              }}
            >
              {readyStatus === 'pending' ? (
                <span style={{
                  width: 16, height: 16, borderRadius: '50%',
                  border: '2px solid rgba(26,19,5,0.30)', borderTopColor: DT.color.goldOnGold,
                  display: 'inline-block', animation: 'spin 0.75s linear infinite',
                }} />
              ) : readyLabel}
            </button>

            {/* Supporting sentence — always shown, never implies a single
                click necessarily starts the round. */}
            <div style={{ textAlign: 'center', fontSize: 11, fontFamily: DT.font.sans, color: DT.color.textMuted }}>
              {t('roundEnd.readyHint')}
            </div>

            {/* Positive countdown: VISUAL ONLY, deliberately outside any live
                region. It ticks every 250ms — wrapping it in role="status"
                would re-announce it to screen readers on every tick. */}
            {countdown != null && countdown > 0 && (
              <div style={{ textAlign: 'center', fontSize: 11, fontFamily: DT.font.sans, color: DT.color.textMuted }}>
                {t('roundEnd.autoContinue', { n: countdown })}
              </div>
            )}

            {/* Ready failure: its own polite status region, separate from the
                countdown — announced once when it appears. */}
            {readyStatus === 'failed' && (
              <div role="status" style={{ textAlign: 'center', fontSize: 11, fontFamily: DT.font.sans, color: DT.color.error }}>
                {t('roundEnd.readyError')}
              </div>
            )}

            {/* Deadline-reached transition: a SEPARATE polite status region
                that mounts once, when the display first switches from the
                positive countdown to this message. Its text stays IDENTICAL
                across every later 250ms tick while still at zero, so the
                node is never recreated and never re-announced. */}
            {countdown != null && countdown <= 0 && (
              <div role="status" style={{ textAlign: 'center', fontSize: 11, fontFamily: DT.font.sans, color: DT.color.textMuted }}>
                {t('roundEnd.startingNext')}
              </div>
            )}
          </div>

        </div>
      </div>
    </GameplayBackdrop>
  );
}
